<template>
    <v-popper2 ref="alertPopper"
               :state="showAlertsMenu"
               slide-from="left"
               direction="vertical"
               position="bottomEnd"
               :offset="16">
        <template #trigger="{popper}">
            <button
                class="btn has-icon stack-m has-badge"
                :class="{'new-alert': newAlert, 'is-positive': newAlert}"
                @mouseenter="openPopper(popper)">
                <v-icon icon="alerts" :class="{'is-ringing': newAlert}"></v-icon>
            </button>
        </template>
        <template #content="{popper}">
            <div class="popover popover-dark" style="width: 300px" @click="popper.close" v-fs-block>
                <div class="popover-arrow is-left-bottom"></div>
                <div class="popover-title">
                    <h2>Alerts</h2>
                </div>
                <template v-if="!hasNew">
                    <div class="popover-content">
                        <p>You have no new alerts</p>
                    </div>
                </template>
                <template v-if="hasNewAlerts">
                    <div class="popover-content popover-menu" :class="{'stack-m': hasJobs}">
                        <alerts-item
                            v-for="alert in alerts"
                            :alert="alert"
                            :key="alert.id"/>
                    </div>
                </template>
                <template v-if="hasJobs">
                    <div class="popover-content popover-menu">
                        <template
                            v-for="job in truncatedCompletedJobs">
                            <li
                                v-if="job.isCompleted"
                                :key="job.id"
                                class="popover-menu-item display-flex flex-flow-column">
                                <span class="text-truncate"><v-icon :icon="job.icon" class="inline-xs"/>{{job.label}}</span>
                                <span class="text-muted">Completed {{$filters.humanizeDuration(job.jobTime)}} ago</span>
                                <a class="align-self-end"
                                v-if="job.downloadLink"
                                :href="job.downloadLink"
                                :download="job.downloadLink"
                                ><strong>Download</strong></a>
                            </li>
                        </template>
                        <div class="popover-menu-item display-flex" v-for="job in getRunning" :key="job.id">
                            <v-icon icon="loading" class="inline-xs is-spinning no-flex"/>
                            <span>Processing {{job.type | unKebabCase}}</span>
                        </div>
                        <div class="popover-menu-item display-flex" v-for="job in getPending" :key="job.id">
                            <v-icon icon="loading" class="inline-xs is-spinning no-flex"/>
                            <span>{{job.type | unKebabCase}} job pending</span>
                        </div>
                    </div>
                </template>
            </div>
        </template>
    </v-popper2>
</template>
<script>
    import Vue from 'vue';
    import vPopper2 from '@/components/popper/v-popper2';
    import moment from 'moment';
    import {mapActions, mapGetters} from 'vuex';
    import {vIcon, vIconSprites} from '@/components/form';
    import AlertsItem from '@/modules/layouts/components/alerts-menu/alerts-item';
    import alertsItems from '@/modules/layouts/components/alerts-menu/alerts-items';

    const MAX_DELAY = 3600000; // one hour
    const INITIAL_POLLING_INTERVAL = 30000; // thirty seconds
    const BACKOFF_MULTIPLIER = 2; // polling interval is doubled after each request

    export default {
        name: 'alerts-menu',
        components: {
            vIconSprites,
            vIcon,
            vPopper2,
            AlertsItem,
        },
        data() {
            return {
                showAlertsMenu: null,
                runningAlerts: false,
                newAlert: false,
                alerts: [],
                polling: null,
                delay: INITIAL_POLLING_INTERVAL,
            };
        },
        watch: {
            latestJobId() {
                if (this.latestJob) {
                    const {type, text} = this.latestJob.toastMessage;
                    this.$toast[type](text);
                }
            },
            timezone(newValue, oldValue) {
                // on logout timezone is null
                if (this.$store.getters['user/isGuest']) {
                    this.alerts = [];
                } else if (!newValue || !oldValue) {
                    this.pullAlerts();
                } else if (newValue.ui !== oldValue.ui) {
                    this.pullAlerts();
                }
            },
            mutedTimezoneNameDiff(newValue, oldValue) {
                if (this.$store.getters['user/isGuest']) {
                    return;
                }
                if (newValue !== oldValue && newValue) {
                    this.pullAlerts();
                }
            },
        },
        computed: {
            ...mapGetters({
                getPending: 'jobs/getPending',
                getRunning: 'jobs/getRunning',
                getCompleted: 'jobs/getCompleted',
                getLastCheckTime: 'jobs/getLastCheckTime',
                expiredTime: 'user/expiredTime',
            }),
            timezone() {
                return this.$store.getters['user/preferences'].timezone;
            },
            mutedTimezoneNameDiff() {
                const preferences = this.$store.getters['user/uiPreferences'];
                const notifications = preferences ? preferences.notifications || {} : {};
                const {mutedTimezoneNameDiff} = notifications;
                return mutedTimezoneNameDiff;
            },
            hasJobs() {
                return !!(this.getPending.length || this.getRunning.length || this.truncatedCompletedJobs.length);
            },
            hasRunningJobs() {
                return !!this.getRunning.length;
            },
            truncatedCompletedJobs() {
                return this.getCompleted
                    .filter(job => job.jobTime.isSameOrAfter(this.$tz()
                        .now()
                        .subtract(1, 'day'), 'day'))
                    .slice(0, 5);
            },
            hasNewAlerts() {
                return !!this.alerts.length;
            },
            hasNewJob() {
                return this.truncatedCompletedJobs
                    .some(job => job.processedOn
                        && (job.processedOn.valueOf() > this.getLastCheckTime.valueOf()));
            },
            latestJob() {
                return this.truncatedCompletedJobs[0];
            },
            latestJobId() {
                if (this.latestJob) {
                    return this.latestJob.id;
                }
                return null;
            },
            hasNew() {
                return this.hasNewAlerts || this.hasNewJob;
            },
        },
        methods: {
            ...mapActions({
                pollBackendJobs: 'jobs/pollBackendJobs',
                resumeFrontendJobs: 'jobs/resumeFrontendJobs',
                updateLastCheckTime: 'jobs/updateLastCheckTime',
            }),
            openPopper(popper) {
                this.updateLastCheckTime();
                popper.open();
                this.newAlert = false;
            },
            closeAlertsMenu() {
                this.$refs.alertPopper.closePopper();
            },
            startPolling() {
                if (this.polling) {
                    this.cancelPolling();
                }
                this.polling = setTimeout(this.pollBackendHandler, this.delay);
            },
            pollBackendHandler() {
                if (this.isSessionExpired()) {
                    this.cancelPolling();
                    return;
                }

                this.pollBackendJobs();

                if (this.hasRunningJobs) {
                    this.delay = Math.min(this.delay * BACKOFF_MULTIPLIER, MAX_DELAY);
                } else {
                    this.delay = MAX_DELAY;
                }
                this.startPolling();
            },
            pullAlerts() {
                const params = {
                    $tz: this.$tz(),
                    $store: this.$store,
                };
                this.alerts = alertsItems.filter(({condition}) => condition(params));
                this.alerts.forEach((alert) => {
                    if (alert.toast) {
                        alert.toast({$toast: this.$toast});
                    }
                });
                if (this.alerts.length) {
                    this.newAlert = true;
                } else if (!this.hasNewJob) {
                    this.newAlert = false;
                }
            },
            cancelPolling() {
                clearTimeout(this.polling);
            },
            resetPolling() {
                this.delay = INITIAL_POLLING_INTERVAL;
                this.startPolling();
            },
            // Do it this way because we need it uncached, unlike a computed property
            isSessionExpired() {
                if (this.expiredTime) {
                    return moment.utc()
                        .isAfter(moment.utc(this.expiredTime));
                }

                return true;
            },
        },
        created() {
            // TODO this hack is needed until we move polling logic to a service worker
            Vue.prototype.$resetPolling = this.resetPolling;
            this.startPolling();
            this.resumeFrontendJobs();
            this.pullAlerts();
        },
        beforeDestroy() {
            this.cancelPolling();
        },
    };
</script>
